import { useState, useRef, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
    content: string;
    placement?: 'top' | 'bottom' | 'left' | 'right';
    customClass?: string;
    children: ReactNode;
}

const Tooltip = ({
    content,
    placement = 'top',
    customClass = '',
    children,
}: TooltipProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [arrowOffset, setArrowOffset] = useState(0);
    const triggerRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isVisible || !triggerRef.current || !tooltipRef.current) return;

        const updatePosition = () => {
            if (!triggerRef.current || !tooltipRef.current) return;

            const triggerRect = triggerRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();

            let top = 0;
            let left = 0;
            let arrowLeft = 0;

            const offset = 8;
            const arrowSize = 4;

            switch (placement) {
                case 'top':
                    top =
                        triggerRect.top +
                        window.scrollY -
                        tooltipRect.height -
                        offset;
                    left =
                        triggerRect.left +
                        window.scrollX +
                        triggerRect.width / 2 -
                        tooltipRect.width / 2;
                    arrowLeft = tooltipRect.width / 2 - arrowSize;
                    break;
                case 'bottom':
                    top = triggerRect.bottom + window.scrollY + offset;
                    left =
                        triggerRect.left +
                        window.scrollX +
                        triggerRect.width / 2 -
                        tooltipRect.width / 2;
                    arrowLeft = tooltipRect.width / 2 - arrowSize;
                    break;
                case 'left':
                    top =
                        triggerRect.top +
                        window.scrollY +
                        triggerRect.height / 2 -
                        tooltipRect.height / 2;
                    left =
                        triggerRect.left +
                        window.scrollX -
                        tooltipRect.width -
                        offset;
                    arrowLeft = 0;
                    break;
                case 'right':
                    top =
                        triggerRect.top +
                        window.scrollY +
                        triggerRect.height / 2 -
                        tooltipRect.height / 2;
                    left = triggerRect.right + window.scrollX + offset;
                    arrowLeft = 0;
                    break;
            }

            // 화면 경계 체크
            const padding = 10;
            const originalLeft = left;

            if (left < padding) {
                left = padding;
            } else if (left + tooltipRect.width > window.innerWidth - padding) {
                left = window.innerWidth - tooltipRect.width - padding;
            }

            // 화살표 위치 조정 (top/bottom일 때만)
            if (placement === 'top' || placement === 'bottom') {
                const shift = originalLeft - left;
                arrowLeft -= shift;
            }

            setPosition({ top, left });
            setArrowOffset(arrowLeft);
        };

        const timeoutId = setTimeout(updatePosition, 0);

        window.addEventListener('scroll', updatePosition, { passive: true });
        window.addEventListener('resize', updatePosition, { passive: true });

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('scroll', updatePosition);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isVisible, placement]);

    const handleMouseEnter = () => setIsVisible(true);
    const handleMouseLeave = () => setIsVisible(false);

    const tooltipElement = isVisible ? (
        <div
            ref={tooltipRef}
            className={`tooltip bs-tooltip-${placement} fade show ${customClass}`}
            role="tooltip"
            style={{
                position: 'absolute',
                top: `${position.top}px`,
                left: `${position.left}px`,
                zIndex: 1080,
            }}
            data-popper-placement={placement}
        >
            <div
                className="tooltip-arrow"
                style={
                    placement === 'top' || placement === 'bottom'
                        ? {
                              position: 'absolute',
                              left: `${arrowOffset}px`,
                          }
                        : undefined
                }
            />
            <div className="tooltip-inner">{content}</div>
        </div>
    ) : null;

    return (
        <>
            <div
                ref={triggerRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{ display: 'inline-block', cursor: 'pointer' }}
            >
                {children}
            </div>
            {tooltipElement && createPortal(tooltipElement, document.body)}
        </>
    );
};

export default Tooltip;
